import {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import css from './note.module.css';
import commonCss from '../common/common.module.css';
import type {Flashcard} from '../../types/Note';
import {generateFlashcards} from '../../services/functionsService';
import {useToast} from '../../context/ToastProvider';
import {useUpgrade} from '../../context/UpgradeProvider';
import {Button, IconButton, Spinner} from '../common/ui';

// Two data sources: the authed note page passes `noteId` (cards generated /
// cached via callable), the public share page passes `initialCards` straight
// from the share doc (no auth, no callable).
export function FlashcardsTab({
  noteId,
  initialCards,
}: {
  noteId?: string;
  initialCards?: Flashcard[] | null;
}) {
  const {t} = useTranslation();
  const showToast = useToast();
  const {handleLimitError} = useUpgrade();

  const [cards, setCards] = useState<Flashcard[] | null>(initialCards ?? null);
  const [loading, setLoading] = useState(!initialCards);
  const [failed, setFailed] = useState(false);
  const [index, setIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);

  const load = useCallback(async () => {
    if (!noteId) {
      return;
    }
    setLoading(true);
    setFailed(false);
    try {
      // Cached server-side in the note's artifacts.
      const result = await generateFlashcards(noteId);
      setCards(result.cards);
      setIndex(0);
      setFlipped(false);
    } catch (e) {
      if (!handleLimitError(e)) {
        console.warn(e);
        showToast(t('ErrorGenerateFlashcard'), 'error');
      }
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [noteId, handleLimitError, showToast, t]);

  useEffect(() => {
    if (!initialCards) {
      void load();
    }
  }, [load, initialCards]);

  if (loading) {
    return (
      <div className={commonCss.emptyState}>
        <Spinner large />
        <p>{t('GeneratingFlashcards')}</p>
      </div>
    );
  }
  if (failed || !cards || cards.length === 0) {
    return (
      <div className={commonCss.emptyState}>
        <p>{t('NoFlashcardsAvailable')}</p>
        {noteId && (
          <Button variant="primary" icon="refresh" onClick={load}>
            {t('TryAgain')}
          </Button>
        )}
      </div>
    );
  }

  const card = cards[index];
  const go = (delta: number) => {
    setIndex(i => Math.min(cards.length - 1, Math.max(0, i + delta)));
    setFlipped(false);
  };

  return (
    <div>
      <p className={css.quizProgress}>
        {index + 1} / {cards.length}
      </p>
      <div className={css.cardStage}>
        <div
          className={flipped ? css.cardFlipped : css.card}
          onClick={() => setFlipped(f => !f)}
        >
          <div className={css.cardFace}>
            <span className={css.cardTitle}>{card.title}</span>
            <span className={css.cardText}>{card.question}</span>
            <span className={css.cardHint}>{t('TapToFlip')}</span>
          </div>
          <div className={css.cardBack}>
            <span className={css.cardTitle}>{t('Answer')}</span>
            <span className={css.cardText}>{card.answer}</span>
            <span className={css.cardHint}>{t('TapToFlip')}</span>
          </div>
        </div>
      </div>
      <div className={css.cardNav}>
        <IconButton
          icon="chevronLeft"
          onClick={() => go(-1)}
          disabled={index === 0}
          title={t('Previous')}
        />
        <Button variant="secondary" onClick={() => setFlipped(f => !f)}>
          {flipped ? t('ShowQuestion') : t('ShowAnswer')}
        </Button>
        <IconButton
          icon="chevronRight"
          onClick={() => go(1)}
          disabled={index === cards.length - 1}
          title={t('Next')}
        />
      </div>
    </div>
  );
}
